type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enableInProduction: boolean;
  logLevel: LogLevel;
  sanitizePatterns: RegExp[];
}

const config: LoggerConfig = {
  enableInProduction: false,
  logLevel: process.env.NODE_ENV === 'development' ? 'debug' : 'error',
  sanitizePatterns: [
    /token/gi,
    /password/gi,
    /secret/gi,
    /key/gi,
    /credential/gi,
    /auth/gi,
    /bearer/gi,
    /api[-_]?key/gi,
  ],
};

const logLevels: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class Logger {
  private shouldLog(level: LogLevel): boolean {
    if (process.env.NODE_ENV === 'production' && !config.enableInProduction) {
      return level === 'error';
    }
    return logLevels[level] >= logLevels[config.logLevel];
  }

  private sanitize(data: any): any {
    if (typeof data === 'string') {
      let sanitized = data;
      config.sanitizePatterns.forEach((pattern) => {
        sanitized = sanitized.replace(pattern, '[REDACTED]');
      });
      return sanitized;
    }

    if (typeof data === 'object' && data !== null) {
      const sanitized: any = Array.isArray(data) ? [] : {};
      for (const key in data) {
        const lowerKey = key.toLowerCase();
        const isSensitive = config.sanitizePatterns.some((pattern) =>
          pattern.test(lowerKey)
        );

        if (isSensitive) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitize(data[key]);
        }
      }
      return sanitized;
    }

    return data;
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      const sanitized = args.map((arg) => this.sanitize(arg));
      console.log(...sanitized);
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      const sanitized = args.map((arg) => this.sanitize(arg));
      console.info(...sanitized);
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      const sanitized = args.map((arg) => this.sanitize(arg));
      console.warn(...sanitized);
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      const sanitized = args.map((arg) => this.sanitize(arg));
      console.error(...sanitized);
    }
  }
}

export const logger = new Logger();
export default logger;